import React, { useEffect, useRef, useState } from 'react'
import { convertStorage, parseFileInfoFromUrl } from '../../utils/IncidentUtil'

import { DDSTypography } from 'den-design-system'
import FilePreviewCard from '../../pages/IncidentPage/FileTypes'
import LinkCard from './LinkCard'
import MediaPreviewGrid from '../common/TextAreaDrafts/MediaPreviewGrid'
import MediaPreviewModal from '../common/TextAreaDrafts/MediaPreviewModal'
import { ProjectType } from '../../enum/ProjectType.enum'
import Spinner from '../common/Spinner/Spinner'
import StringConstants from '../../constants/StringConstants'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

interface MediaFile {
  id: any
  file: File
  fileUrl: string
}

interface DetailsProps {
  details: {
    priority: string
    identifiedOn: string
    longDescription: string
    exceptionLogId: any
    links: string[]
    exceptionType?: string
    attachedFiles?: any
    rootCause?: any
    solution?: any
    resolvedAt: any
  }
  loading: boolean
  deleteLink: any
  status: any
}

const priorityColorMap: Record<string, string> = {
  P0: pallete.colors.warningRed1,
  P1: pallete.colors.warningRed2,
  P2: pallete.colors.warningRed3,
  P3: pallete.colors.warningBlue
}
const Details: React.FC<DetailsProps> = ({
  details,
  deleteLink,
  loading,
  status
}) => {
  const store = useStoreContext()
  const navigate = useNavigate()

  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY

  const routeExceptionLog = (id: number, type: string) => {
    store.exceptionStore.setId(id.toString())
    store.exceptionStore.setType(type)
    sessionStorage.setItem(
      StringConstants.ACTIVE_TAB,
      StringConstants.TAB_VALUES.EXCEPTION
    )
    const route = replaceUrl(
      routeConstants.ERROR_DETAILS,
      store.scopeStore.getScope()['organization-id'] as string,
      store.scopeStore.getScope()['project-id'] as string,
      store.scopeStore.getScope()['environment-id'] as string
    ).replace(':id', String(id))
    navigate(route)
  }
  const [previewFile, setPreviewFile] = useState<any>(null)
  const [isPreviewOpen, setPreviewOpen] = useState(false)

  const handleClosePreview = () => {
    setPreviewOpen(false)
    store.uiStore.setSidebarExpanded(true)
    setPreviewFile(null)
  }

  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (isPreviewOpen && previewFile?.includes('video')) {
      const videoElement = videoRef.current
      if (videoElement) {
        videoElement
          .play()
          .catch((error: any) => console.error('Autoplay prevented:', error))
      }
    }
  }, [isPreviewOpen, previewFile])

  const handlePreview = (url: string) => {
    setPreviewOpen(true)
    store.uiStore.setSidebarExpanded(false)
    setPreviewFile(url)
  }

  return (
    <>
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full h-[100%] py-[24px] '>
          <div className='gap-y-[24px] flex flex-col'>
            <div className='flex flex-row justify-between w-full'>
              <div className='flex flex-row gap-x-[8px] items-center custom-label'>
                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  className='custom-label'
                  color={pallete.colors.textDark3}
                >
                  Priority :
                </DDSTypography.Title>
                <div
                  className='rounded-[100px] py-[4px] px-[8px] custom-label w-[60px]'
                  style={{
                    backgroundColor: priorityColorMap[details.priority]
                  }}
                >
                  <DDSTypography.Paragraph
                    variant='medium'
                    size='caption'
                    className='text-center whitespace-nowrap flex justify-center'
                    color={pallete.colors.white}
                  >
                    {details?.priority}
                  </DDSTypography.Paragraph>
                </div>
              </div>
              <div className='flex flex-row justify-center items-baseline'>
                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  className='custom-label whitespace-nowrap'
                  color={pallete.colors.textDark3}
                >
                  {status === 'Resolved' ? 'Resolved on ' : 'Identified on'} :
                </DDSTypography.Title>
                <DDSTypography.Paragraph
                  size='para'
                  className='custom-label whitespace-nowrap pl-[4px]'
                  color={pallete.colors.textDark5}
                >
                  {status !== 'Resolved'
                    ? timeZone(
                        store.userStore.getOrganizationPreference().timeZone,
                        details.identifiedOn,
                        timeFormats.fullMonthDateYear
                      )
                    : timeZone(
                        store.userStore.getOrganizationPreference().timeZone,
                        details.resolvedAt,
                        timeFormats.fullMonthDateYearTime
                      )}
                </DDSTypography.Paragraph>
              </div>
            </div>
            {details?.exceptionLogId && (
              <div className='flex flex-col gap-y-[8px]'>
                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  {isFullStackProject ? 'Exception' : 'Error'}
                </DDSTypography.Title>
                <a
                  onClick={() => {
                    routeExceptionLog(
                      details.exceptionLogId,
                      details.exceptionLogId as string
                    )
                  }}
                  style={{ color: pallete.colors.primary2 }}
                >
                  <div className='cursor-pointer'>
                    <DDSTypography.Paragraph
                      variant='regular'
                      size='para'
                      className='underline'
                      color={pallete.colors.primary2}
                    >
                      {details?.exceptionType}
                    </DDSTypography.Paragraph>
                  </div>
                </a>
              </div>
            )}
            <div className='flex flex-col gap-y-[8px]'>
              <DDSTypography.Title
                type='h5'
                variant='semiBold'
                color={pallete.colors.textDark3}
                className='custom-label'
              >
                Description
              </DDSTypography.Title>
              <DDSTypography.Paragraph
                variant='regular'
                size='para'
                color={pallete.colors.textDark5}
                style={{
                  overflowWrap: 'break-word'
                }}
              >
                {details?.longDescription}
              </DDSTypography.Paragraph>
            </div>
            {details?.rootCause &&
              details?.solution &&
              status === 'Resolved' && (
                <>
                  <div className='flex flex-col gap-y-[8px]'>
                    <DDSTypography.Title
                      type='h5'
                      variant='semiBold'
                      color={pallete.colors.textDark3}
                      className='custom-label'
                    >
                      Root Cause
                    </DDSTypography.Title>
                    <DDSTypography.Paragraph
                      variant='regular'
                      size='para'
                      color={pallete.colors.textDark5}
                      style={{
                        overflowWrap: 'break-word'
                      }}
                    >
                      {details.rootCause}
                    </DDSTypography.Paragraph>
                  </div>

                  <div className='flex flex-col gap-y-[8px]'>
                    <DDSTypography.Title
                      type='h5'
                      variant='semiBold'
                      color={pallete.colors.textDark3}
                      className='custom-label'
                    >
                      Solution
                    </DDSTypography.Title>
                    <DDSTypography.Paragraph
                      variant='regular'
                      size='para'
                      color={pallete.colors.textDark5}
                      style={{
                        overflowWrap: 'break-word'
                      }}
                    >
                      {details.solution}
                    </DDSTypography.Paragraph>
                  </div>
                </>
              )}

            <div className='flex flex-col py-[12px] gap-y-[8px]'>
              {(details?.links?.length > 0 ||
                details?.attachedFiles?.length > 0) && (
                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Attachments & Reference links
                </DDSTypography.Title>
              )}
              {details?.links?.map((link, index) => (
                <LinkCard key={index} link={link} onClose={deleteLink} />
              ))}
            </div>
          </div>
          <MediaPreviewGrid
            attachedFiles={details?.attachedFiles}
            onPreview={(url) => handlePreview(url)}
            variant='default'
          />

          <div className='flex flex-wrap pt-[12px] gap-[12px]'>
            {details?.attachedFiles?.map((media: any) => {
              const isHeic =
                media.includes('heic') ||
                media.includes('heif') ||
                media.endsWith('.heic') ||
                media.endsWith('.heif')
              const isImage = media.includes('image/') && !isHeic
              const isVideo = media.includes('video')
              const fileInfo = parseFileInfoFromUrl(media)

              return (
                <React.Fragment key={media}>
                  {!isImage && !isVideo && (
                    <FilePreviewCard
                      file={{
                        name: fileInfo.name,
                        type: fileInfo.type,
                        url: media,
                        size: convertStorage(fileInfo.size)
                      }}
                      uploadType='display'
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>

          <MediaPreviewModal
            isPreviewOpen={isPreviewOpen}
            handleClosePreview={handleClosePreview}
            previewFile={previewFile}
          />
        </div>
      )}
    </>
  )
}

export default Details
